import { Stock } from "./Stock";
import { Stores } from "./Stores";
import { RootState } from "redux/store";
import useToggle from "hooks/useToggle";
import { Manufacturer } from "./Manufacturer";
import { useState, useRef, useEffect } from "react";
import { FormInput, Title, Spinner, Modal } from "components";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Row, Tab, Nav, Form, Button } from "react-bootstrap";
import { updatePreferredBrandField, getPreferredBrandDetails, cleanPreferredBrands, updatePreferredBrand, createPreferredBrand, getPreferredBrands } from "redux/slices/PreferredBrands";
import { PreferredBrandAddUpdate } from "models/dbModels/preferredBrands/PreferredBrand";

export function NewPreferredBrand() {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { preferredBrandId } = useParams();
	const [loading, setLoading] = useState(false);
	const [isValidated, setValidated] = useToggle();
	const [showWarning, setShowWarning] = useState(false);
	const [isNewPreferredBrand, setIsNewPreferredBrand] = useState<boolean>(true);
	const [duplicatePreferredBrands, setDuplicatePreferredBrands] = useState<any>([]);
	const preferredBrands = useAppSelector((state: RootState) => state.preferredBrands);

	useEffect(() => {
		dispatch(cleanPreferredBrands());
		if (preferredBrandId && !isNaN(parseInt(preferredBrandId))) {
			dispatch(getPreferredBrandDetails({ ids: [parseInt(preferredBrandId)], loadDetails: ['Manufacturers', 'Stock', 'Stores'] })).then((res) => {
				setIsNewPreferredBrand(true);
			});
		} else {
			setIsNewPreferredBrand(false);
			navigate("/app/preferred-brand/new");
		}
	}, [preferredBrandId]);

	function getPreferredBrandStocks() {
		const stocks: any = [];
		const preferredStock: any = [...preferredBrands.genericCategories];
		preferredStock.forEach((preferredStock: any) => {
			preferredStock.stocks.forEach((stock: any, index: number) => {
				stocks.push({
					...stock,
					rank: index + 1,
				});
			});
		});
		return stocks;
	}

	async function storesWarning() {
		setDuplicatePreferredBrands([]);
		preferredBrands.storesTable.length > 0 && await getDuplicatePreferredBrands();
		if (preferredBrands.storesTable.length === 0 && duplicatePreferredBrands.length === 0) {
			handleSubmit();
		} else {
			setShowWarning(true);
		}
	}



	async function getDuplicatePreferredBrands() {
		await dispatch(getPreferredBrands({
			storeIds: preferredBrands.storesTable.map((store: any) => store.id),
			loadDetails: ["Stores"],
		})).then((res) => {
			const duplicatePreferredBrand = res.data.reduce((acc: string[], store) => {
				if (preferredBrands.id !== store.id) acc.push(store.name);
				return acc;
			}, []);
			setDuplicatePreferredBrands(duplicatePreferredBrand ? duplicatePreferredBrand : []);
		});
	}

	function handleSubmit() {
		setLoading(true);
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const manufacturers = preferredBrands.pbsManufacturers.map((manufacturer: any, index: number) => {
				return {
					...manufacturer,
					rank: index + 1,
				}
			});

			const newPreferredBrand: PreferredBrandAddUpdate = {
				id: preferredBrands.id,
				name: preferredBrands.name,
				preferredManufacturers: manufacturers,
				preferredStock: getPreferredBrandStocks(),
				stores: [
					...preferredBrands.stores,
					...preferredBrands.storesList,
				],
			};

			if (!isNewPreferredBrand) {
				dispatch(createPreferredBrand(newPreferredBrand)).then(() => handleUpdate());
			} else {
				dispatch(updatePreferredBrand(newPreferredBrand)).then(() => handleUpdate());
			}
		} else {
			setLoading(false);
		}
		setValidated();
	}

	function handleUpdate() {
		setLoading(false);
		navigate("/app/preferred-brands");
	}

	function handleChange(key: string, value: string) {
		dispatch(updatePreferredBrandField({ key: key, value: value }));
	}

	function updateManufacturersIndexes() {
		let manufacturers: any[] = [...preferredBrands.pbsManufacturers];
		manufacturers.forEach((manufacturer: any, index: number) => {
			manufacturers[index] = { ...manufacturer, rank: index + 1 };
		});
		dispatch(updatePreferredBrandField({ key: "pbsManufacturers", value: manufacturers }));
	}

	return (
		<>
			<Title pageTitle={isNewPreferredBrand ? "Update Preferred Brand" : "New Preferred Brand"} crumbs={[{ name: "Preferred Brands", path: "/app/preferred-brands" }]} />
			<Card>
				<Card.Body className="pe-2">
					<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
						<Row>
							<h4 className="mt-2">Preferred Brand Details</h4>
							<Col md={6}>
								<Col md={8}>
									<FormInput
										required
										label="Name"
										type="text"
										name="preferred-brand-name"
										placeholder="Enter preferred brand name"
										containerClass={"mb-3"}
										value={preferredBrands.name}
										onChange={(e: any) => handleChange("name", e.target.value)}
									/>
								</Col>
							</Col>
							<Col md={6} className="d-flex flex-row-reverse">
							</Col>
						</Row>
						<Row>
							<Modal
								title="Confirm"
								show={showWarning}
								buttonType="primary"
								buttonText="Continue"
								buttonCloseText="Cancel"
								handleSubmit={handleSubmit}
								handleClose={() => setShowWarning(false)}
								description={
									<>
										<p>You are about to override all existing preferred brands at the selected store(s).</p>
										{duplicatePreferredBrands.length > 0 && <>
											<p>The selected store(s) will also be moved from their existing preferred brands group:</p>
											<ul>{duplicatePreferredBrands.map((duplicates: any, index: number) => (<li key={index} className="mb-0">{duplicates}</li>))}</ul>
										</>}
										<p>Are you sure you want to continue?</p>
									</>
								}
							/>
						</Row>
						<Row className="pt-1 p-0 m-0">
							<Tab.Container defaultActiveKey="1">
								<Row className="mb-1 pe-0 me-0">
									<Nav id="stocks-nav" as="ul" variant="pills" className="nav nav-pills bg-nav-pills nav-justified mb-3">
										<Nav.Item as="li" className="nav-item">
											<Nav.Link href="#" eventKey="1" className="nav-link rounded-0">Manufacturer Level</Nav.Link>
										</Nav.Item>
										<Nav.Item as="li">
											<Nav.Link href="#" eventKey="2" className="nav-link rounded-0">Stock Level</Nav.Link>
										</Nav.Item>
										<Nav.Item as="li">
											<Nav.Link href="#" eventKey="3" className="nav-link rounded-0">Stores</Nav.Link>
										</Nav.Item>
									</Nav>
									<Tab.Content className="ps-0 pe-0">
										<Tab.Pane eventKey="1">
											<Manufacturer />
										</Tab.Pane>
										<Tab.Pane eventKey="2">
											<Stock />
										</Tab.Pane>
										<Tab.Pane eventKey="3">
											<Stores />
										</Tab.Pane>
									</Tab.Content>
								</Row>
							</Tab.Container>
						</Row>
						<Row className="mt-2 pe-2 pb-2">
							<Col sm={6}>
								<Link to="/app/preferred-brands" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold" onClick={() => dispatch(cleanPreferredBrands())}>
									<i className="mdi mdi-arrow-left"></i> Back to All Preferred Brands {" "}
								</Link>
							</Col>
							<Col sm={6} className="d-flex flex-row-reverse">
								<Button variant="primary" className="btn cta-button" disabled={loading} onClick={storesWarning}>
									{loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
									{loading ? <>Saving...</> : <>Save</>}
								</Button>
							</Col>
						</Row>
					</Form>
				</Card.Body>
			</Card>
		</>
	);
}